import React, { useState } from 'react';
import { 
  Typography, 
  Box, 
  Grid, 
  Paper, 
  Button, 
  Card, 
  CardContent, 
  CardActions,
  Container,
  Divider,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MicIcon from '@mui/icons-material/Mic';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import ShareIcon from '@mui/icons-material/Share';
import BugReportIcon from '@mui/icons-material/BugReport';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ComingSoonIcon from '@mui/icons-material/Schedule';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import appConfig from '../config/appConfig';

// Styles
const FeatureIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 60,
  height: 60,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  marginBottom: theme.spacing(2),
}));

const WelcomeHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0, 6),
  textAlign: 'center',
}));

const AboutPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [betaAlertOpen, setBetaAlertOpen] = useState(true);

  const handleChangelogOpen = () => {
    setChangelogOpen(true);
  };

  const handleChangelogClose = () => {
    setChangelogOpen(false);
  };

  const handleBetaAlertClose = () => {
    setBetaAlertOpen(false);
  };

  const features = [
    {
      title: 'Transcription Audio',
      description: 'Convertissez vos réunions en texte facilement avec la technologie d\'AssemblyAI qui permet une détection des individus ou Whisper d\'OpenAI.',
      icon: <MicIcon sx={{ fontSize: 30 }} />,
    },
    {
      title: 'Résumés Automatiques',
      description: 'Générez des résumés concis de vos réunions grâce à l\'intelligence artificielle.',
      icon: <FormatQuoteIcon sx={{ fontSize: 30 }} />,
    },
    {
      title: 'Export Word',
      description: 'Exportez vos transcriptions et résumés au format Word pour une utilisation facile.',
      icon: <InsertDriveFileIcon sx={{ fontSize: 30 }} />,
    },
    {
      title: 'Partage Sécurisé',
      description: 'Partagez vos réunions avec vos collègues en toute sécurité.',
      icon: <ShareIcon sx={{ fontSize: 30 }} />,
    },
  ];

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Bannière version Beta */}
      {/* {betaAlertOpen && (
        <Alert 
          severity="warning" 
          variant="filled"
          icon={<BugReportIcon />}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleBetaAlertClose}
              sx={{ 
                color: '#6a3b00',
                minWidth: 'auto',
                p: 0.5,
                '&:hover': {
                  backgroundColor: 'rgba(106, 59, 0, 0.1)'
                }
              }}
            >
              <CloseIcon fontSize="small" />
            </Button>
          }
          sx={{ 
            borderRadius: 0, 
            mb: 3,
            boxShadow: 2,
            background: 'linear-gradient(90deg, #f8b133 0%, #f9a825 100%)',
            color: '#6a3b00',
            py: { xs: 1, sm: 2 }, // Padding vertical réduit sur mobile
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              py: { xs: 0, sm: 1 } // Padding vertical interne réduit sur mobile
            },
            '& .MuiAlert-icon': {
              color: '#6a3b00'
            },
            '& .MuiAlert-action': {
              alignSelf: 'flex-start',
              pt: { xs: 0.5, sm: 1 }
            }
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            width: '100%',
            flexDirection: { xs: 'column', sm: 'row' }, // Colonne sur mobile, ligne sur desktop
            gap: { xs: 0.5, sm: 2 }
          }}>
            <AlertTitle sx={{ 
              mr: { xs: 0, sm: 2 }, 
              fontWeight: 'bold', 
              color: '#6a3b00',
              fontSize: { xs: '0.875rem', sm: '1rem' } // Taille de police réduite sur mobile
            }}>
              Version Beta {appConfig.version}
            </AlertTitle>
            <Typography variant="body2" sx={{ 
              color: '#6a3b00',
              fontSize: { xs: '0.75rem', sm: '0.875rem' }, // Taille de police réduite sur mobile
              textAlign: 'center'
            }}>
              Cette application est en cours de développement. Certaines fonctionnalités peuvent contenir des bugs.
            </Typography>
            <Link 
              component="button"
              variant="body2"
              onClick={() => navigate('/changelog')}
              sx={{ 
                ml: { xs: 0, sm: 2 }, 
                color: '#6a3b00', 
                fontWeight: 'bold',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: { xs: '0.75rem', sm: '0.875rem' }, // Taille de police réduite sur mobile
                '&:hover': {
                  color: '#4a2700'
                }
              }}
            >
              Voir les nouveautés
            </Link>
          </Box>
        </Alert>
      )} */}

      <WelcomeHeader>
        <Typography variant="h3" component="h1" gutterBottom>
          À propos de TranscriptionAI
        </Typography>
        <Typography variant="h5" component="h2" color="textSecondary" paragraph>
          Votre solution intelligente pour la transcription et le résumé de réunions
        </Typography>
        <Box sx={{ mt: 4 }}>
          <Button 
            variant="contained" 
            size="large" 
            color="primary" 
            onClick={() => navigate('/meetings')}
            sx={{ mr: 2 }}
          >
            Nouvelle Réunion
          </Button>
          <Button 
            variant="outlined" 
            size="large" 
            onClick={() => navigate('/meetings')}
          >
            Mes Réunions
          </Button>
        </Box>
      </WelcomeHeader>

      <Container maxWidth="lg" sx={{ mt: 6, mb: 6 }}>
        <Typography variant="h4" align="center" gutterBottom>
          Fonctionnalités
        </Typography>
        <Typography variant="body1" align="center" color="textSecondary" paragraph>
          Découvrez comment cet outil peut simplifier votre gestion de réunions
        </Typography>

        <Grid container spacing={4} sx={{ mt: 2 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <FeatureIcon>
                    {feature.icon}
                  </FeatureIcon>
                  <Typography gutterBottom variant="h5" component="h2" align="center">
                    {feature.title}
                  </Typography>
                  <Typography align="center">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Paper 
          sx={{ 
            mt: 8, 
            p: 4, 
            borderRadius: 2,
            background: 'linear-gradient(45deg, #3f51b5 30%, #5677fc 90%)',
            color: 'white',
          }}
        >
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h4" gutterBottom>
                Commencez dès maintenant
              </Typography>
              <Typography variant="body1" paragraph>
                Importez votre premier fichier audio et découvrez la puissance de la transcription et des résumés automatiques.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
              <Button 
                variant="contained" 
                size="large" 
                color="secondary" 
                onClick={() => navigate('/meetings/new')}
                sx={{ 
                  color: 'primary.main', 
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: 'white',
                  }
                }}
              >
                Nouvelle Réunion
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Footer avec informations de version et développeur */}
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="body2" color="text.secondary">
            {appConfig.appName} v{appConfig.version}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Développé par {appConfig.developer}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {appConfig.copyright}
          </Typography>
        </Box>
      </Container>

      {/* Dialog Changelog */}
      <Dialog 
        open={changelogOpen} 
        onClose={handleChangelogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          backgroundColor: 'primary.main',
          color: 'white'
        }}>
          <Box>
            <Typography variant="h5" component="div">
              Nouveautés v{appConfig.version}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Interface transcription premium et contrôles audio avancés
            </Typography>
          </Box>
          <Button 
            onClick={handleChangelogClose}
            sx={{ color: 'white', minWidth: 'auto', p: 1 }}
          >
            <CloseIcon />
          </Button>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 4 }}>
          {/* Nouveautés actuelles */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3, color: 'text.primary', marginTop: 2 }}>
              Nouveautés v{appConfig.version}
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="🎨 Design glassmorphism premium"
                  secondary="Nouveau header avec effet glassmorphism, dégradés subtils et interface moderne inspirée 2025"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="🎵 Lecture audio intégrée"
                  secondary="Boutons play/pause pour écouter chaque segment de la transcription avec gestion intelligente"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="👥 Attribution des participants"
                  secondary="Menu contextuel Material-UI pour identifier facilement les locuteurs avec support mobile optimisé"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="📱 UX mobile perfectionnée"
                  secondary="Correction des débordements, scroll intelligent et interface adaptative sans défilement horizontal"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="⚡ Navigation optimisée"
                  secondary="Bouton scroll-to-top intelligent qui cible le bon conteneur selon le contexte"
                />
              </ListItem>
            </List>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Version précédente v0.4.4 */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2, color: 'text.secondary' }}>
              Améliorations v0.4.4-beta
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="📱 Bottom Navigation Mobile"
                  secondary="Ajout d'un menu de navigation en bas sur mobile pour les pages réunions et modèles"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="🎯 Navigation adaptative"
                  secondary="Masquage automatique des tabs sur mobile, remplacés par le bottom navigation"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="⚡ Navigation fluide"
                  secondary="Suppression des rechargements de page lors des changements d'onglets"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="📐 UX mobile optimisée"
                  secondary="Interface entièrement repensée pour une meilleure ergonomie sur appareils tactiles"
                />
              </ListItem>
            </List>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Fonctionnalités à venir */}
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3, color: 'text.primary' }}>
              Fonctionnalités à venir
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <ComingSoonIcon fontSize="small" sx={{ color: 'warning.main' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="💬 Chat intelligent"
                  secondary="Ajout d'un chat pour poser des questions concernant la réunion et générer de nouveaux résumés"
                />
                <Chip label="Bientôt" size="small" color="warning" variant="outlined" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <ComingSoonIcon fontSize="small" sx={{ color: 'warning.main' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="📈 Statistiques avancées"
                  secondary="Statistiques d'utilisation détaillées par utilisateur et organisation"
                />
                <Chip label="Bientôt" size="small" color="warning" variant="outlined" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <ComingSoonIcon fontSize="small" sx={{ color: 'warning.main' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="🔍 Recherche intelligente"
                  secondary="Recherche sémantique dans les transcriptions et résumés avec IA"
                />
                <Chip label="Bientôt" size="small" color="warning" variant="outlined" />
              </ListItem>
            </List>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, backgroundColor: 'grey.50' }}>
          <Button 
            onClick={handleChangelogClose} 
            variant="contained" 
            color="primary"
            sx={{ minWidth: 100 }}
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AboutPage; 